import { useState, type CSSProperties, type ReactNode } from 'react';
import { Check, ExternalLink, Info } from 'lucide-react';
import { useOnboardingStore } from '../../stores/onboardingStore';
import {
  CONDITIONS,
  CONSENTS,
  CONSENT_REVERSIBLE_NOTE,
  IDENTITY_OPTIONS,
  METANOIA_PROVIDES,
  REASONS,
  ROLES_BRIDGE,
  ROLES_FOOTER,
  SPECTRUM,
  STAGES,
  TRUST_POINTS,
  YOU_PROVIDE,
  legalUrl,
  spectrumAt,
  type LegalDocument,
  type OnboardingStepKey,
} from '../../constants/onboarding';
import { summarySentence } from '../../utils/onboardingFlow';
import { CityField } from './CityField';

/**
 * The body of each onboarding screen — everything between the eyebrow rail and
 * the Continue button, which the shell in `OnboardingFlow` owns.
 *
 * ⭐ EVERY SCREEN'S CONTENT IS `my-auto`, NOT FLEX-GROWN. Growing the content to
 * fill the scroll area piled each screen against the rail and left the whole
 * surplus as one void at the bottom — very visible on a 1040px-tall window,
 * where About and Data & terms had ~400px of nothing under them. An auto margin
 * splits the surplus above and below instead, and resolves to ZERO the moment
 * the content is taller than the space, so a short screen still scrolls from
 * the top rather than clipping its own first line.
 *
 * Implements the `docs/design/onboarding-v2/` artboards (16 of them: eight at
 * 1280×900 and the same eight at 390×844).
 *
 * ⭐⭐ TWO MODES, NOT ONE SHAPE. Every screen used to be title + lede + a column
 * of rounded cards — seven near-identical rows for the conditions, seven for
 * the journey, nine for motivation. The TEACHING steps (conditions, roles,
 * journey) now go full-bleed and draw their idea; the ANSWERING steps
 * (motivation, about, consents) are a contained centre column. The sequence
 * already alternates between the two, so the rhythm costs nothing — and the
 * mode comes straight off the catalog's existing `teaching` flag rather than a
 * second list of step names that could drift out of step with it.
 *
 * ⚠️ THE HEADLINE LIVES HERE, NOT IN THE SHELL. It used to be shell-owned, in a
 * left pane; in this design its size and position are part of each screen's
 * argument (92px and centred on Welcome, beside the ring on Journey, inside the
 * 640px form column on About), so it belongs to the body.
 *
 * ⚠️ Colours are explicit rather than themed — see the note in
 * `OnboardingFlow.tsx`. This surface is dark in every theme.
 */

// ── Tokens ──────────────────────────────────────────────────────────────────

/**
 * ⚠️ Brother 1816 is the app's real display face, loaded from cdnfonts in
 * `index.html`. The artboards were drawn with Outfit as a stand-in because the
 * font is not in the repo, so it stays in the stack as the closest fallback:
 * every fixed-height screen here was re-measured with Brother 1816 loaded.
 *
 * ⚠️ NOT the `.font-brand` class — that bakes in `letter-spacing: .2em` for the
 * wordmark, which is wrong for a 76px headline.
 */
export const DISPLAY = "'Brother 1816', Outfit, system-ui, -apple-system, sans-serif";

export const INK = {
  white: '#FFFFFF',
  bright: '#CBD5E1',
  body: '#9AA3B2',
  muted: '#8A93A3',
  faint: '#6B7280',
  dim: '#5E6474',
} as const;

export const HAIRLINE = 'rgba(255,255,255,.07)';
export const CTA_GRADIENT = 'linear-gradient(135deg, #3CC2DD, #A739E2)';

/**
 * Each screen carries its own light source. Welcome and Ready are the two
 * bookends and are the only ones lit from a different place — Ready's glow
 * comes from BELOW, which is the whole reason the last screen feels like an
 * arrival rather than another step.
 */
const BACKGROUNDS: Record<OnboardingStepKey, string> = {
  welcome: 'radial-gradient(ellipse 1240px 700px at 50% -12%, rgba(167,57,226,.18) 0%, rgba(7,7,14,0) 66%)',
  motivation: 'radial-gradient(ellipse 900px 520px at 50% -8%, rgba(60,194,221,.10) 0%, rgba(7,7,14,0) 70%)',
  conditions: 'radial-gradient(ellipse 1200px 640px at 22% -8%, rgba(60,194,221,.11) 0%, rgba(7,7,14,0) 68%)',
  roles: 'radial-gradient(ellipse 1200px 640px at 78% -8%, rgba(245,140,86,.11) 0%, rgba(7,7,14,0) 66%)',
  journey: 'radial-gradient(ellipse 1100px 620px at 28% -6%, rgba(167,57,226,.12) 0%, rgba(7,7,14,0) 66%)',
  about: 'radial-gradient(ellipse 900px 520px at 50% -8%, rgba(140,91,226,.11) 0%, rgba(7,7,14,0) 70%)',
  consents: 'radial-gradient(ellipse 900px 520px at 50% -8%, rgba(60,194,221,.10) 0%, rgba(7,7,14,0) 70%)',
  ready: 'radial-gradient(ellipse 1240px 720px at 50% 108%, rgba(167,57,226,.22) 0%, rgba(7,7,14,0) 62%)',
};

/** The painted background for a step, over the flat base. */
export const stepBackground = (key: OnboardingStepKey): string =>
  `${BACKGROUNDS[key]}, #07070E`;

const hexA = (hex: string, a: number): string => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

/**
 * A near-black tint of `hex`, for the numeral printed inside a filled spectrum
 * swatch. Derived rather than tabulated so that a change to {@link SPECTRUM}
 * cannot leave seven hand-picked inks behind pointing at the old colours.
 */
const onColour = (hex: string): string => {
  const n = parseInt(hex.slice(1), 16);
  const c = (v: number) => Math.round(v * 0.1).toString(16).padStart(2, '0').toUpperCase();
  return `#${c((n >> 16) & 255)}${c((n >> 8) & 255)}${c(n & 255)}`;
};

// ── Small shared pieces ─────────────────────────────────────────────────────

function Display({
  children, className, style,
}: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <h1
      className={`m-0 text-white ${className ?? ''}`}
      style={{ fontFamily: DISPLAY, ...style }}
    >
      {children}
    </h1>
  );
}

function Lede({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={`m-0 text-[13px] leading-[1.5] lg:text-[16px] lg:leading-[1.6] ${className ?? ''}`}
      style={{ color: INK.body }}
    >
      {children}
    </p>
  );
}

function FieldLabel({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between">
      <label className="text-[10px] uppercase tracking-[.13em] lg:text-[11px] lg:tracking-[.14em]" style={{ color: INK.body }}>
        {children}
      </label>
      {hint}
    </div>
  );
}

const OPTIONAL = (
  <span className="normal-case tracking-normal" style={{ color: INK.dim }}> — optional</span>
);

/** ⭐ The birthday and the city are required now (owner, 2026-09-18). */
const REQUIRED = (
  <span className="text-[10px] uppercase tracking-[.1em]" style={{ color: '#3CC2DD' }}>Required</span>
);

const fieldStyle: CSSProperties = {
  background: 'rgba(255,255,255,.03)',
  border: '1px solid rgba(255,255,255,.11)',
  color: INK.white,
  /* ⚠️ Without this the native date picker renders its own light-mode panel on
     a screen that is dark in every theme. */
  colorScheme: 'dark',
};

/**
 * A link to a public document — or its name in plain text when there is no
 * document to link to.
 *
 * ⚠️⚠️ NEVER RENDERS A DEAD LINK. All three documents are published today, but
 * the mechanism is deliberately kept: asking someone to accept a document they
 * cannot read is not a consent at all, so an unpublished one has to say so
 * rather than 404.
 */
function DocumentLink({ doc }: { doc: LegalDocument }) {
  const href = legalUrl(doc);
  if (!href) {
    return (
      <span className="text-[11.5px] lg:text-[13px]" style={{ color: INK.faint }}>
        {doc.label} <span className="text-[10px]">(not published yet)</span>
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 text-[11.5px] transition-colors hover:text-[#CA88EE] lg:text-[13px]"
      style={{ color: '#8ADAEB' }}
    >
      {doc.label}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

// ── The cycle ring ──────────────────────────────────────────────────────────

const RING_R = 180;
const RING_CENTRE = 230;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R;

/** Centre of stage `i`'s arc, in the ring's own 460×460 coordinates. */
function stagePoint(i: number, n: number): { x: number; y: number } {
  const deg = -90 + (360 / n) * (i + 0.5);
  const rad = (deg * Math.PI) / 180;
  return {
    x: RING_CENTRE + RING_R * Math.cos(rad),
    y: RING_CENTRE + RING_R * Math.sin(rad),
  };
}

/**
 * The seven stages drawn as a ring.
 *
 * ⭐⭐ THE SHAPE IS THE SENTENCE. This screen says "a cycle, not a ladder" and
 * was drawn as a vertical list — which is a ladder. The ring, with a dashed
 * return from the last stage back to the first, is what makes the copy true.
 *
 * ⚠️ Geometry is COMPUTED from `STAGES.length`, not tabulated. The artboard
 * hardcodes seven arc offsets and seven numeral positions; an eighth stage
 * would have left the drawing silently disagreeing with the list beside it.
 */
function CycleRing({
  size, stroke, nodeR = 0, nodeFont = 0, centreFont = 0, chrome = true, className,
}: {
  size: number;
  stroke: number;
  nodeR?: number;
  nodeFont?: number;
  centreFont?: number;
  /** The numerals, the dashed return and the centre line. Off on step 8, where
   *  the same ring closes into a single finished object. */
  chrome?: boolean;
  /** Overrides the drawn size. The geometry is a viewBox, so this only scales. */
  className?: string;
}) {
  const n = STAGES.length;
  const segment = RING_CIRCUMFERENCE / n;
  const dash = segment - 15.6;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 460 460"
      role="img"
      aria-label="The stages, arranged as a repeating cycle"
      className={`max-w-full ${className ?? ''}`}
    >
      <g transform={`rotate(-90 ${RING_CENTRE} ${RING_CENTRE})`}>
        {STAGES.map((s, i) => (
          <circle
            key={s.name}
            cx={RING_CENTRE}
            cy={RING_CENTRE}
            r={RING_R}
            fill="none"
            stroke={spectrumAt(i, n)}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${RING_CIRCUMFERENCE - dash}`}
            strokeDashoffset={-segment * i}
          />
        ))}
      </g>

      {chrome && (
        <>
          <g fontFamily={DISPLAY} fontSize={nodeFont} fontWeight={600} textAnchor="middle">
            {STAGES.map((s, i) => {
              const { x, y } = stagePoint(i, n);
              const colour = spectrumAt(i, n);
              return (
                <g key={s.name}>
                  <circle cx={x} cy={y} r={nodeR} fill={colour} />
                  <text x={x} y={y + nodeFont * 0.35} fill={onColour(colour)}>{s.numeral}</text>
                </g>
              );
            })}
          </g>

          {/* The return: the last stage feeds back into the first. Drawn across
              the top, where the ring opens between the last node and the first. */}
          <path d="M 176 44 A 200 200 0 0 1 284 44" fill="none" stroke={INK.faint} strokeWidth={1.6} strokeDasharray="4 5" />
          <path d="M 278 37 l 8 7 -8 7" fill="none" stroke={INK.faint} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />

          <text x={RING_CENTRE} y={222} textAnchor="middle" fontFamily={DISPLAY} fontSize={centreFont} fontWeight={500} fill={INK.white} letterSpacing="0.13em">A CYCLE,</text>
          <text x={RING_CENTRE} y={222 + centreFont * 1.5} textAnchor="middle" fontFamily={DISPLAY} fontSize={centreFont} fontWeight={500} fill={INK.faint} letterSpacing="0.13em">NOT A LADDER</text>
        </>
      )}
    </svg>
  );
}

// ── The screens ─────────────────────────────────────────────────────────────

export function OnboardingStepBody({ stepKey }: { stepKey: OnboardingStepKey }) {
  const answers = useOnboardingStore((s) => s.answers);
  const setAnswers = useOnboardingStore((s) => s.setAnswers);
  const toggleReason = useOnboardingStore((s) => s.toggleReason);
  const setConsent = useOnboardingStore((s) => s.setConsent);

  /* Phone only: the Journey screen shows stage NAMES and puts the descriptions
     behind a tap. Seven descriptions at 11px is a wall nobody reads. */
  const [openStage, setOpenStage] = useState<string | null>(null);

  switch (stepKey) {
    // ── 01 · Welcome ─────────────────────────────────────────────────────
    case 'welcome':
      return (
        <div className="my-auto flex flex-col gap-6 lg:gap-9">
          <Display className="text-[46px] font-light leading-[.99] tracking-[-.032em] lg:text-[76px] lg:leading-[.98] lg:tracking-[-.035em]">
            Design your<br />
            <span
              className="font-normal"
              style={{
                background: 'linear-gradient(100deg, #3CC2DD, #A739E2 54%, #F58C56)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              }}
            >
              becoming.
            </span>
          </Display>

          <p className="m-0 max-w-[620px] text-[15px] leading-[1.55] lg:text-[18px]" style={{ color: INK.body }}>
            You are capable of becoming a better version of yourself. Metanoia is the
            structure that makes that a practice rather than an intention.
          </p>

          {/* What the next few minutes are, stated once and honestly. Three
              columns on the desktop screen, three bullets on the phone —
              genuinely different shapes for the same three facts. */}
          <div className="hidden gap-10 pt-2 lg:flex">
            {PROMISES.map((p) => (
              <div key={p.title} className="flex max-w-[210px] flex-col gap-[7px]">
                <span className="text-[15px] font-medium text-white" style={{ fontFamily: DISPLAY }}>{p.title}</span>
                <span className="text-[13px] leading-[1.5]" style={{ color: INK.muted }}>{p.body}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-3 lg:hidden">
            {PROMISES.map((p) => (
              <div key={p.title} className="flex items-baseline gap-3">
                <span className="h-[5px] w-[5px] shrink-0 rounded-full" style={{ background: p.colour }} />
                <span className="text-[13px] leading-[1.45]" style={{ color: INK.muted }}>
                  <span className="text-white">{p.title}.</span> {p.body}
                </span>
              </div>
            ))}
          </div>
        </div>
      );

    // ── 02 · Motivation (answering) ──────────────────────────────────────
    case 'motivation':
      return (
        <div className="my-auto flex flex-col gap-6 pt-2 lg:gap-8 lg:pt-4">
          <div className="flex flex-col gap-2.5 lg:gap-3">
            <Display className="text-[32px] font-normal leading-[1.12] tracking-[-.02em] lg:text-[44px] lg:leading-[1.1] lg:tracking-[-.022em]">
              What brought you here?
            </Display>
            <Lede>Choose as many as apply. This decides what Metanoia puts in front of you first.</Lede>
          </div>

          {/* ⭐ Chips, wrapped. Nine full-width rows needed a scroll on the
              phone and read as a form; the whole set fits in one look here. */}
          <div className="flex flex-wrap content-start gap-2.5 lg:gap-[11px]">
            {REASONS.map((r, i) => {
              const on = answers.reasons.includes(r.key);
              const c = spectrumAt(i, REASONS.length);
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => toggleReason(r.key)}
                  aria-pressed={on}
                  className="inline-flex items-center gap-2 rounded-full border px-4 py-3 text-[14px] transition-colors lg:px-5 lg:py-3.5 lg:text-[15px]"
                  style={{
                    color: on ? INK.white : INK.bright,
                    background: on ? hexA(c, 0.15) : 'rgba(255,255,255,.03)',
                    borderColor: on ? c : 'rgba(255,255,255,.1)',
                  }}
                >
                  {on && <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} style={{ color: c }} />}
                  {r.label}
                </button>
              );
            })}
          </div>

          {/* Reflected back immediately, so the answer visibly does something.
              The phone says the same thing in the footer's one spare line. */}
          <div
            className="hidden items-start gap-3 rounded-xl border px-5 py-4 lg:flex"
            style={{ borderColor: 'rgba(255,255,255,.08)', background: 'rgba(255,255,255,.02)' }}
          >
            <Info className="mt-[2px] h-[17px] w-[17px] shrink-0" strokeWidth={1.6} style={{ color: INK.faint }} />
            <span className="text-[14px] leading-[1.55]" style={{ color: INK.body }}>
              {summarySentence(answers.reasons)}
              {answers.reasons.length > 0 && ' We will open with those.'}
            </span>
          </div>
        </div>
      );

    // ── 03 · Conditions (teaching) ───────────────────────────────────────
    case 'conditions':
      return (
        <div className="my-auto flex flex-col">
          <div className="flex max-w-[900px] flex-col gap-2.5 pt-2 lg:gap-4 lg:pt-4">
            <Display className="text-[27px] font-normal leading-[1.15] tracking-[-.018em] lg:text-[46px] lg:leading-[1.08] lg:tracking-[-.022em]">
              Motivation starts change.<br />Conditions sustain it.
            </Display>
            <Lede className="max-w-[660px]">
              Metanoia builds five of the seven with you. The last two only you can supply.
            </Lede>
          </div>

          <ConditionsSequence />
        </div>
      );

    // ── 04 · Roles (teaching) ────────────────────────────────────────────
    case 'roles':
      return (
        <div className="my-auto flex flex-col">
          <div className="flex flex-col gap-2.5 pt-2 lg:gap-3.5 lg:pt-4">
            <Display className="text-[29px] font-normal leading-[1.13] tracking-[-.02em] lg:text-[46px] lg:leading-[1.08] lg:tracking-[-.022em]">
              No system can live your life for you.
            </Display>
            <Lede className="max-w-[620px]">{ROLES_BRIDGE}</Lede>
          </div>

          {/* ⚠️⚠️ KEPT SIDE BY SIDE AT 390px. Stacking is the obvious phone move
              and was rejected: the CONTRAST between the two columns is the
              whole argument, and stacked they become two unrelated lists. */}
          {/* ⚠️ NOT flex-grown on the phone. Stretched into the space a 844px screen
              leaves over, the two cards became mostly-empty 600px panels; sized to
              their content they stay equal to each other, which is the only
              equality this screen needs. */}
          <div className="mt-4 grid grid-cols-2 gap-3 lg:mt-9 lg:gap-7">
            <RoleColumn title="Metanoia provides" items={METANOIA_PROVIDES} colour="#3CC2DD" />
            <RoleColumn title="You provide" items={YOU_PROVIDE} colour="#F58C56" />
          </div>

          <p
            className="m-0 mt-4 text-center text-[15px] font-light leading-[1.35] lg:mt-8 lg:text-[20px]"
            style={{ fontFamily: DISPLAY, color: INK.bright }}
          >
            {ROLES_FOOTER}
          </p>
        </div>
      );

    // ── 05 · Journey (teaching) ──────────────────────────────────────────
    case 'journey':
      return (
        <div className="my-auto flex flex-col lg:flex-row lg:items-center lg:gap-14">
          {/* Phone: headline, then the ring, then the names. */}
          <Display className="pt-2 text-[29px] font-normal leading-[1.13] tracking-[-.02em] lg:hidden">
            Seven stages, moved through repeatedly.
          </Display>

          <div className="mt-3 flex shrink-0 justify-center lg:mt-0">
            <span className="lg:hidden">
              <CycleRing size={224} stroke={12} nodeR={21} nodeFont={15} centreFont={20} />
            </span>
            {/* ⚠️ Scales with the window. Fixed at 410 the ring left the stage
                list a 446px column at 1024 wide, every description wrapped, and
                the screen overflowed a 768px-tall window by 59px. */}
            <span className="hidden lg:inline">
              <CycleRing size={410} stroke={9} nodeR={16} nodeFont={12} centreFont={14} className="h-auto w-[320px] xl:w-[410px]" />
            </span>
          </div>

          <div className="flex min-w-0 flex-grow flex-col gap-5 lg:gap-6">
            <Display className="hidden text-[42px] font-normal leading-[1.1] tracking-[-.022em] lg:block">
              Seven stages, moved<br />through repeatedly.
            </Display>

            {/* Desktop: the full table. */}
            <div className="hidden flex-col lg:flex">
              {STAGES.map((s, i) => (
                <div
                  key={s.name}
                  className="grid grid-cols-[26px_104px_minmax(0,1fr)] items-baseline gap-4 border-b py-[9px] last:border-b-0"
                  style={{ borderColor: 'rgba(255,255,255,.06)' }}
                >
                  <span className="text-[11px]" style={{ fontFamily: DISPLAY, color: spectrumAt(i, STAGES.length) }}>{s.numeral}</span>
                  <span className="text-[14px] font-semibold tracking-[.07em] text-white" style={{ fontFamily: DISPLAY }}>{s.name}</span>
                  {/* ⭐ The artboard drops the tool list on the desktop screen.
                      It is kept: the ring is 410px tall and the column beside it
                      only reached 298px, so naming what each stage is DONE WITH
                      cost nothing but the measurement that proved it. */}
                  <span className="flex flex-col">
                    <span className="text-[13px] leading-[1.45]" style={{ color: INK.muted }}>{s.desc}</span>
                    <span className="text-[11px] leading-[1.4]" style={{ color: INK.dim }}>{s.tools}</span>
                  </span>
                </div>
              ))}
            </div>

            {/* Phone: names only, descriptions behind a tap. */}
            <div className="mt-4 flex flex-col gap-3 lg:hidden">
              <div className="grid grid-cols-2 content-start gap-x-3 gap-y-2">
                {STAGES.map((s, i) => {
                  const open = openStage === s.name;
                  return (
                    <button
                      key={s.name}
                      type="button"
                      onClick={() => setOpenStage(open ? null : s.name)}
                      aria-expanded={open}
                      className="flex items-baseline gap-2.5 rounded-md px-1 py-0.5 text-left transition-colors"
                      style={{ background: open ? 'rgba(255,255,255,.06)' : 'transparent' }}
                    >
                      <span className="w-5 shrink-0 text-[10px]" style={{ fontFamily: DISPLAY, color: spectrumAt(i, STAGES.length) }}>{s.numeral}</span>
                      <span className="text-[13px] font-semibold tracking-[.06em] text-white" style={{ fontFamily: DISPLAY }}>{s.name}</span>
                    </button>
                  );
                })}
              </div>
              <StageDetail name={openStage} />
            </div>
          </div>
        </div>
      );

    // ── 06 · About you (answering) ───────────────────────────────────────
    /* ⭐ Name, birthday and identity were three consecutive screens ("About you
       1 of 3", "2 of 3", "3 of 3") — a progress bar admitting the sequence was
       too long. One short form: only the name is required. */
    case 'about':
      return (
        <div className="my-auto flex flex-col gap-5 pt-2 lg:gap-7 lg:pt-4">
          <div className="flex flex-col gap-2.5 lg:gap-3">
            <Display className="text-[30px] font-normal leading-[1.12] tracking-[-.02em] lg:text-[42px] lg:leading-[1.1] lg:tracking-[-.022em]">
              What should we call you?
            </Display>
            <Lede>Your name, birthday and city set up your day. The rest you can skip, or change later.</Lede>
          </div>

          <div className="flex flex-col gap-4 lg:gap-5">
            <div className="flex flex-col gap-1.5 lg:gap-2">
              <FieldLabel
                hint={<span className="text-[10px] uppercase tracking-[.1em]" style={{ color: '#3CC2DD' }}>Required</span>}
              >
                Preferred name
              </FieldLabel>
              <input
                type="text"
                value={answers.preferredName}
                onChange={(e) => setAnswers({ preferredName: e.target.value })}
                placeholder="How you like to be addressed"
                autoComplete="given-name"
                className="h-[52px] w-full rounded-xl px-4 text-[16px] outline-none transition-colors lg:px-[18px]"
                style={{
                  ...fieldStyle,
                  background: 'rgba(255,255,255,.045)',
                  border: `1px solid ${answers.preferredName.trim() ? 'rgba(60,194,221,.45)' : 'rgba(255,255,255,.11)'}`,
                }}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2 lg:gap-[18px]">
              <div className="flex flex-col gap-1.5 lg:gap-2">
                <FieldLabel>Full name{OPTIONAL}</FieldLabel>
                <input
                  type="text"
                  value={answers.fullName}
                  onChange={(e) => setAnswers({ fullName: e.target.value })}
                  placeholder="Your full name"
                  autoComplete="name"
                  className="h-[52px] w-full rounded-xl px-4 text-[16px] outline-none lg:px-[18px]"
                  style={fieldStyle}
                />
              </div>
              <div className="flex flex-col gap-1.5 lg:gap-2">
                <FieldLabel hint={REQUIRED}>Date of birth</FieldLabel>
                <input
                  type="date"
                  value={answers.dateOfBirth}
                  onChange={(e) => setAnswers({ dateOfBirth: e.target.value })}
                  aria-label="Date of birth"
                  className="h-[52px] w-full rounded-xl px-4 text-[16px] outline-none lg:px-[18px]"
                  style={{
                    ...fieldStyle,
                    border: `1px solid ${answers.dateOfBirth ? 'rgba(60,194,221,.45)' : 'rgba(255,255,255,.11)'}`,
                  }}
                />
              </div>
            </div>

            {/* ⭐⭐ THE CITY (owner, 2026-09-18) — it sets the day clock and the
                prayer times from the first day, and the console reads the
                country for its analysis view. */}
            <div className="flex flex-col gap-1.5 lg:gap-2">
              <FieldLabel hint={REQUIRED}>Your city</FieldLabel>
              <CityField
                value={answers.city}
                onChange={(city) => setAnswers({ city })}
                fieldStyle={fieldStyle}
              />
            </div>

            <div className="flex flex-col gap-1.5 lg:gap-2">
              <FieldLabel>How would you like to identify?{OPTIONAL}</FieldLabel>
              <div className="grid grid-cols-2 gap-2.5 lg:gap-[11px]">
                {IDENTITY_OPTIONS.map((g, i) => {
                  const on = answers.gender === g.key;
                  const c = spectrumAt(i, IDENTITY_OPTIONS.length);
                  return (
                    <button
                      key={g.key}
                      type="button"
                      aria-pressed={on}
                      /* ⚠️ Tapping the chosen one again CLEARS it back to null.
                         Unanswered is a distinct, meaningful state; without this
                         the first tap is irreversible. */
                      onClick={() => setAnswers({ gender: on ? null : g.key })}
                      className="flex h-[52px] items-center gap-3 rounded-xl border px-4 text-[15px] transition-colors lg:px-[18px]"
                      style={{
                        color: on ? INK.white : INK.bright,
                        background: on ? hexA(c, 0.13) : 'rgba(255,255,255,.03)',
                        borderColor: on ? c : 'rgba(255,255,255,.11)',
                      }}
                    >
                      <span
                        className="grid h-[19px] w-[19px] shrink-0 place-items-center rounded-full"
                        style={{
                          border: on ? 'none' : '1.5px solid rgba(255,255,255,.25)',
                          background: on ? c : 'transparent',
                        }}
                      >
                        {on && <Check className="h-[11px] w-[11px]" strokeWidth={3.4} style={{ color: onColour(c) }} />}
                      </span>
                      {g.label}
                    </button>
                  );
                })}
              </div>
              <p className="m-0 mt-1 text-[12px] leading-[1.45] lg:text-[13px]" style={{ color: INK.faint }}>
                Shapes how Metanoia speaks to you and which health tools it offers. Tap your
                answer again to clear it.
              </p>
            </div>
          </div>
        </div>
      );

    // ── 07 · Data & terms (answering) ────────────────────────────────────
    case 'consents':
      return (
        <div className="my-auto flex flex-col gap-3.5 pt-2 lg:gap-3.5 lg:pt-3 xl:gap-4">
          <div className="flex flex-col gap-2 lg:gap-3">
            <Display className="text-[28px] font-normal leading-[1.14] tracking-[-.02em] lg:text-[30px] lg:leading-[1.12] xl:text-[34px]">
              How Metanoia treats your information.
            </Display>
            {/* ⭐ ABOVE the list, not below it. This is the sentence that lowers
                the stakes of ticking a box, so it has to be read BEFORE the
                decision — and at the bottom it fell below the fold on exactly
                the screen where reassurance matters most. */}
            <Lede className="!text-[12.5px] lg:!text-[15px]">
              <span className="hidden lg:inline">In plain language, before the documents. </span>
              {CONSENT_REVERSIBLE_NOTE}
            </Lede>
          </div>

          <div
            className="grid gap-y-1 border-y py-3 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-1 lg:py-2 xl:gap-x-10 xl:gap-y-1.5 xl:py-2.5"
            style={{ borderColor: HAIRLINE }}
          >
            {TRUST_POINTS.map((t) => (
              <div key={t.title} className="flex gap-2.5 lg:gap-3">
                {/* ⚠️ 66px, as drawn, was sized for four short labels; the catalog has
                    five and "Communities" ran straight into its own definition. */}
                <span className="w-[82px] shrink-0 text-[11.5px] font-semibold text-white lg:w-[86px] lg:text-[13px]">{t.title}</span>
                <span className="text-[11.5px] leading-[1.4] lg:text-[13px] lg:leading-[1.5]" style={{ color: INK.muted }}>{t.body}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            {CONSENTS.map((c, i) => {
              const on = answers.consents[c.kind] === true;
              const colour = spectrumAt(i, CONSENTS.length);
              return (
                <div
                  key={c.kind}
                  className="rounded-xl border px-3 py-3 lg:px-4 lg:py-2 xl:px-[18px] xl:py-2.5"
                  style={{
                    borderColor: on ? hexA(colour, 0.5) : 'rgba(255,255,255,.1)',
                    background: on ? hexA(colour, 0.08) : 'rgba(255,255,255,.03)',
                  }}
                >
                  {/* ⚠️ The row is a button and the document links sit OUTSIDE
                      it. A link nested inside a button is invalid markup and
                      unreachable by keyboard — on the one screen where reading
                      the document before agreeing is the entire point. */}
                  <button
                    type="button"
                    onClick={() => setConsent(c.kind, !on)}
                    aria-pressed={on}
                    className="flex w-full gap-3 text-left lg:gap-3.5"
                  >
                    <span
                      className="mt-[1px] grid h-[19px] w-[19px] shrink-0 place-items-center rounded-[5px] lg:h-5 lg:w-5"
                      style={{
                        background: on ? colour : 'transparent',
                        border: on ? 'none' : '1.5px solid rgba(255,255,255,.25)',
                      }}
                    >
                      {on && <Check className="h-3 w-3" strokeWidth={3.4} style={{ color: onColour(colour) }} />}
                    </span>
                    <span className="flex min-w-0 flex-col gap-1">
                      <span className="text-[13.5px] leading-[1.3] lg:text-[15px]" style={{ color: on ? INK.white : INK.bright }}>
                        {c.label}{' '}
                        <span className="whitespace-nowrap text-[9px] uppercase tracking-[.12em] lg:text-[10px] lg:tracking-[.14em]" style={{ color: INK.faint }}>
                          {c.required ? 'Required' : 'Optional'}
                        </span>
                      </span>
                      {/* ⚠️ The two REQUIRED rows hide this line on the phone —
                          their documents are the thing that must be tappable
                          there, and the detail is one tap away in either one. */}
                      <span
                        className={`text-[11.5px] leading-[1.4] lg:text-[13px] lg:leading-[1.5] ${c.required ? 'hidden lg:block' : ''}`}
                        style={{ color: INK.muted }}
                      >
                        {c.detail}
                      </span>
                    </span>
                  </button>
                  {c.documents.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 pl-[31px] lg:pl-[34px]">
                      {c.documents.map((d) => <DocumentLink key={d.label} doc={d} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );

    // ── 08 · Ready ───────────────────────────────────────────────────────
    case 'ready':
      return (
        <div className="my-auto flex flex-col items-center gap-6 text-center lg:gap-7">
          <CycleRing size={96} stroke={29} chrome={false} />

          <div className="flex flex-col items-center gap-3.5 lg:gap-4">
            <Display className="text-[38px] font-light leading-[1.06] tracking-[-.028em] lg:text-[56px] lg:leading-[1.04] lg:tracking-[-.03em]">
              Your journey has begun.
            </Display>
            {/* ⭐ The one place the motivation answers are reflected back. The
                standalone summary screen was cut; saying it here costs no extra
                step and lands where it means something. */}
            <p className="m-0 max-w-[620px] text-[15px] leading-[1.55] lg:text-[18px]" style={{ color: INK.bright }}>
              {summarySentence(answers.reasons)}
            </p>
            <p className="m-0 max-w-[560px] text-[13.5px] leading-[1.55] lg:text-[16px] lg:leading-[1.6]" style={{ color: INK.muted }}>
              Next is your Roadmap — the path through Metanoia, one step at a time.
            </p>
          </div>

          {/* ⚠️⚠️ PAST TENSE WOULD BE A LIE HERE. Nothing has been written yet:
              `complete()` runs on "Enter Metanoia", so a chip reading "Consents
              recorded" would be claiming a database write that has not happened
              and might still fail. These say what the person has SUPPLIED,
              which is true at the moment they are painted. */}
          <div className="flex w-full flex-col gap-2 px-2 lg:w-auto lg:flex-row lg:gap-3 lg:px-0">
            {SET_UP.map((s) => (
              <span
                key={s.label}
                className="inline-flex items-center gap-2 rounded-full border px-3.5 py-2.5 text-[12.5px] lg:px-4 lg:text-[13px]"
                style={{ borderColor: 'rgba(255,255,255,.09)', background: 'rgba(255,255,255,.02)', color: INK.body }}
              >
                <Check className="h-3 w-3 shrink-0" strokeWidth={3} style={{ color: s.colour }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>
      );

    default:
      return null;
  }
}

// ── Step 1 · the three promises ─────────────────────────────────────────────

const PROMISES: readonly { title: string; body: string; colour: string }[] = [
  { title: 'Eight short screens', body: 'Three explain how the system thinks. Three ask you something.', colour: SPECTRUM[0] },
  { title: 'Nothing is permanent', body: 'Every answer here is changeable later in Settings.', colour: SPECTRUM[3] },
  { title: 'Only one is required', body: 'What to call you. The rest you can skip.', colour: SPECTRUM[6] },
] as const;

// ── Step 8 · what the person supplied ───────────────────────────────────────

const SET_UP: readonly { label: string; colour: string }[] = [
  { label: 'Profile set', colour: SPECTRUM[0] },
  { label: 'Preferences chosen', colour: SPECTRUM[3] },
  { label: 'Consents answered', colour: SPECTRUM[6] },
] as const;

// ── Step 3 · the seven conditions ───────────────────────────────────────────

/**
 * ⚠️⚠️ THE LAST TWO ARE DASHED, AND THAT IS AN ARGUMENT RATHER THAN A STYLE.
 * Metanoia builds five of the seven with you and cannot supply Commitment or
 * Consistency. Counted from the END of the catalog so the treatment follows the
 * two conditions rather than the numbers 6 and 7.
 */
const selfSupplied = (i: number): boolean => i >= CONDITIONS.length - 2;

function ConditionsSequence() {
  const n = CONDITIONS.length;
  const ramp = `linear-gradient(90deg, ${SPECTRUM.join(', ')})`;

  return (
    <>
      {/* Desktop: a horizontal sequence with the spectrum running through it. */}
      <div className="relative mt-12 hidden lg:block">
        <div
          className="absolute left-[22px] right-[22px] top-[21px] h-px opacity-[.55]"
          style={{ background: ramp }}
        />
        <div className="relative grid grid-cols-7 gap-5">
          {CONDITIONS.map((c, i) => {
            const colour = spectrumAt(i, n);
            const open = selfSupplied(i);
            return (
              <div key={c.name} className="flex flex-col gap-4">
                <div
                  className="grid h-11 w-11 place-items-center rounded-full text-[13px] font-semibold"
                  style={{
                    fontFamily: DISPLAY,
                    background: open ? hexA(colour, 0.1) : colour,
                    border: open ? `1.5px dashed ${colour}` : 'none',
                    color: open ? colour : onColour(colour),
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[17px] font-medium text-white" style={{ fontFamily: DISPLAY }}>{c.name}</span>
                  <span className="text-[13px] leading-[1.5]" style={{ color: INK.muted }}>{c.desc}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* The legend is what makes the dashed treatment mean something. */}
        <div className="mt-10 flex items-center gap-7">
          <div className="flex items-center gap-2.5">
            <span className="h-3 w-3 rounded-full" style={{ background: SPECTRUM[2] }} />
            <span className="text-[13px]" style={{ color: INK.muted }}>Metanoia builds these with you</span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="h-3 w-3 rounded-full" style={{ border: `1.5px dashed ${SPECTRUM[6]}` }} />
            <span className="text-[13px]" style={{ color: INK.muted }}>Only you can supply these</span>
          </div>
        </div>
      </div>

      {/* Phone: the same sequence turned on its side. The connector runs
          vertically and carries the same spectrum, so it reads as the same
          diagram rather than a different one. */}
      <div className="mt-4 lg:hidden">
        {/* ⚠️ The connector is positioned against the ROWS, not against the
            flex-grown wrapper. Anchored to the wrapper it ran on past the last
            node to the bottom of whatever space the screen happened to have. */}
        <div className="relative">
          <div
            className="absolute bottom-[14px] left-[14px] top-[14px] w-px opacity-50"
            style={{ background: `linear-gradient(180deg, ${SPECTRUM.join(', ')})` }}
          />
          <div className="relative flex flex-col gap-2">
            {CONDITIONS.map((c, i) => {
              const colour = spectrumAt(i, n);
              const open = selfSupplied(i);
              return (
                <div key={c.name} className="flex items-start gap-3.5">
                  <span
                    className="grid h-[29px] w-[29px] shrink-0 place-items-center rounded-full text-[11px] font-semibold"
                    style={{
                      fontFamily: DISPLAY,
                      background: open ? hexA(colour, 0.1) : colour,
                      border: open ? `1.5px dashed ${colour}` : 'none',
                      color: open ? colour : onColour(colour),
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="flex min-w-0 flex-col gap-0.5 pt-[3px]">
                    <span className="text-[15px] font-medium text-white" style={{ fontFamily: DISPLAY }}>{c.name}</span>
                    <span className="text-[12px] leading-[1.4]" style={{ color: INK.muted }}>{c.desc}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-2.5 flex items-center gap-2.5 pl-[43px]">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ border: `1.5px dashed ${SPECTRUM[6]}` }} />
          <span className="text-[12px]" style={{ color: INK.faint }}>Dashed: only you can supply these.</span>
        </div>
      </div>
    </>
  );
}

// ── Step 4 · the two columns ────────────────────────────────────────────────

/**
 * ⚠️⚠️ THE CONDITION TAGS ARE THE REPETITION, AND THE REPETITION IS THE POINT.
 * Commitment and Consistency were dashed on the previous screen as the two
 * Metanoia cannot supply; here they reappear on the side they belong to, still
 * dashed, carrying their condition number. Looked up in {@link CONDITIONS} by
 * name rather than tabulated, so the tag cannot drift away from the screen it
 * refers back to — and NEVER de-duplicated away.
 */
function conditionTag(item: string): { number: string; colour: string } | null {
  const i = CONDITIONS.findIndex((c) => c.name === item);
  if (i < 0 || !selfSupplied(i)) return null;
  return { number: String(i + 1).padStart(2, '0'), colour: spectrumAt(i, CONDITIONS.length) };
}

function RoleColumn({
  title, items, colour,
}: { title: string; items: readonly string[]; colour: string }) {
  return (
    <div
      className="flex flex-col gap-3 rounded-[14px] border px-[15px] py-4 lg:gap-4 lg:rounded-2xl lg:px-6 lg:py-5 xl:px-8 xl:py-6"
      style={{
        borderColor: hexA(colour, 0.23),
        background: `linear-gradient(160deg, ${hexA(colour, 0.08)}, ${hexA(colour, 0)} 62%)`,
      }}
    >
      <div className="flex min-h-[30px] items-start gap-2 lg:min-h-0 lg:items-center lg:gap-3">
        <span className="mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full lg:mt-0 lg:h-2 lg:w-2" style={{ background: colour }} />
        <span
          className="text-[9.5px] uppercase tracking-[.13em] lg:text-[12px] lg:tracking-[.18em]"
          style={{ fontFamily: DISPLAY, color: colour }}
        >
          {title}
        </span>
      </div>
      <div className="flex flex-col">
        {items.map((item) => {
          const tag = conditionTag(item);
          return (
            <div
              key={item}
              className="flex flex-col gap-1 border-b py-2 last:border-b-0 lg:flex-row lg:items-center lg:gap-3 lg:py-2 xl:py-2.5"
              style={{ borderColor: 'rgba(255,255,255,.06)' }}
            >
              <span
                className="text-[17px] font-normal leading-[1.2] text-white lg:text-[22px] lg:leading-normal xl:text-[25px]"
                style={{ fontFamily: DISPLAY }}
              >
                {item}
              </span>
              {tag && (
                <span
                  className="self-start whitespace-nowrap rounded-full border border-dashed px-1.5 py-0.5 text-[8.5px] uppercase tracking-[.11em] lg:px-2.5 lg:py-[3px] lg:text-[10px] lg:tracking-[.13em]"
                  style={{ color: tag.colour, borderColor: hexA(tag.colour, 0.6) }}
                >
                  Condition {tag.number}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Step 5 · the tapped stage (phone) ───────────────────────────────────────

function StageDetail({ name }: { name: string | null }) {
  const stage = STAGES.find((s) => s.name === name);
  if (!stage) {
    return (
      <p className="m-0 text-[11px]" style={{ color: INK.dim }}>Tap any stage to read more</p>
    );
  }
  return (
    <div
      className="rounded-xl border px-3.5 py-3"
      style={{ borderColor: 'rgba(255,255,255,.09)', background: 'rgba(255,255,255,.03)' }}
    >
      <p className="m-0 text-[12px] leading-[1.45]" style={{ color: INK.muted }}>{stage.desc}</p>
      <p className="m-0 mt-1 text-[11px]" style={{ color: INK.dim }}>{stage.tools}</p>
    </div>
  );
}
